import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const AdminTrackerContext = createContext();

export const useAdminTracker = () => {
  const context = useContext(AdminTrackerContext);
  if (!context) {
    throw new Error('useAdminTracker must be used within an AdminTrackerProvider');
  }
  return context;
};

export const AdminTrackerProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  // API base URL for admin tracker endpoints
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';
  
  // Determine the correct base URL based on login type
  const getTrackerBaseUrl = useCallback(() => {
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/admin-tracker/api/v1/admin-tracker`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/admin-tracker/api/v1/admin-tracker`;
    } else {
      throw new Error('Invalid login type for admin tracker access');
    }
  }, [API_BASE_URL, loginType]);

  // Helper function to handle API responses
  const handleApiResponse = async (response) => {
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }
    
    return data;
  };

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validate user access
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can access admin tracker');
    }
  }, [loginType]);

  // Create a new tracker record
  const createTracker = useCallback(async (trackerData) => {
    validateAccess();

    if (!trackerData.admin_id) {
      throw new Error('Admin ID is required');
    }

    if (!trackerData.change_type) {
      throw new Error('Change type is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getTrackerBaseUrl();
      const response = await makeAuthenticatedRequest(baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          admin_id: trackerData.admin_id,
          change_type: trackerData.change_type,
          change_reason: trackerData.change_reason || '',
        }),
      });
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to create tracker record';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getTrackerBaseUrl]);

  // Get all tracker records with pagination and filters
  const getTrackers = useCallback(async (options = {}) => {
    validateAccess();

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.offset) queryParams.append('offset', options.offset);
      if (options.adminId) queryParams.append('admin_id', options.adminId);
      if (options.changeType) queryParams.append('change_type', options.changeType);
      if (options.dateFrom) queryParams.append('date_from', options.dateFrom);
      if (options.dateTo) queryParams.append('date_to', options.dateTo);
      if (options.search) queryParams.append('search', options.search);

      const baseUrl = getTrackerBaseUrl();
      const fullUrl = queryParams.toString() ? `${baseUrl}?${queryParams.toString()}` : baseUrl;
      
      const response = await makeAuthenticatedRequest(fullUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch tracker records';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getTrackerBaseUrl]);

  // Get tracker record by ID
  const getTrackerById = useCallback(async (trackerId) => {
    validateAccess();
    
    if (!trackerId) {
      throw new Error('Tracker ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getTrackerBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/tracker/${encodeURIComponent(trackerId)}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get tracker record by ID';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getTrackerBaseUrl]);

  // Get tracker records by admin ID
  const getTrackersByAdminId = useCallback(async (adminId, options = {}) => {
    validateAccess();
    
    if (!adminId) {
      throw new Error('Admin ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.offset) queryParams.append('offset', options.offset);
      if (options.changeType) queryParams.append('change_type', options.changeType);
      if (options.dateFrom) queryParams.append('date_from', options.dateFrom);
      if (options.dateTo) queryParams.append('date_to', options.dateTo);
      if (options.search) queryParams.append('search', options.search);

      const baseUrl = getTrackerBaseUrl();
      const url = `${baseUrl}/admin/${encodeURIComponent(adminId)}`;
      const fullUrl = queryParams.toString() ? `${url}?${queryParams.toString()}` : url;
      
      const response = await makeAuthenticatedRequest(fullUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get tracker records by admin ID';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getTrackerBaseUrl]);

  // Get tracker statistics
  const getTrackerStats = useCallback(async () => {
    validateAccess();

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getTrackerBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/stats`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get tracker statistics';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getTrackerBaseUrl]);

  // Get tracker statistics by admin ID
  const getTrackerStatsByAdminId = useCallback(async (adminId) => {
    validateAccess();
    
    if (!adminId) {
      throw new Error('Admin ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getTrackerBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/admin/${encodeURIComponent(adminId)}/stats`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get tracker statistics by admin ID';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getTrackerBaseUrl]);

  // Get all unique change types
  const getChangeTypes = useCallback(async () => {
    validateAccess();

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getTrackerBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/change-types`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get change types';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getTrackerBaseUrl]);

  // Delete tracker record by ID
  const deleteTracker = useCallback(async (trackerId) => {
    validateAccess();
    
    if (!trackerId) {
      throw new Error('Tracker ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getTrackerBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/tracker/${encodeURIComponent(trackerId)}`,
        {
          method: 'DELETE',
        }
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete tracker record';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getTrackerBaseUrl]);

  // Get trackers by change type
  const getTrackersByChangeType = useCallback(async (changeType, options = {}) => {
    validateAccess();
    
    if (!changeType) {
      throw new Error('Change type is required');
    }

    return await getTrackers({ ...options, changeType });
  }, [getTrackers, validateAccess]);

  // Get trackers by date range
  const getTrackersByDateRange = useCallback(async (dateFrom, dateTo, options = {}) => {
    validateAccess();
    
    return await getTrackers({ 
      ...options, 
      dateFrom, 
      dateTo 
    });
  }, [getTrackers, validateAccess]);

  // Search trackers
  const searchTrackers = useCallback(async (searchTerm, options = {}) => {
    validateAccess();
    
    if (!searchTerm || searchTerm.length < 2) {
      throw new Error('Search term must be at least 2 characters');
    }

    return await getTrackers({ ...options, search: searchTerm });
  }, [getTrackers, validateAccess]);

  // Get recent trackers (last N records)
  const getRecentTrackers = useCallback(async (limit = 10) => {
    validateAccess();
    return await getTrackers({ limit, offset: 0 });
  }, [getTrackers, validateAccess]);

  // Get trackers for today
  const getTodayTrackers = useCallback(async (options = {}) => {
    validateAccess();
    
    const today = new Date().toISOString().split('T')[0];
    return await getTrackers({ ...options, dateFrom: today, dateTo: today });
  }, [getTrackers, validateAccess]);

  // Get trackers for this month
  const getThisMonthTrackers = useCallback(async (options = {}) => {
    validateAccess();
    
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    
    return await getTrackers({ ...options, dateFrom: firstDay, dateTo: lastDay });
  }, [getTrackers, validateAccess]);

  // Export tracker data
  const exportTrackerData = useCallback(async (filters = {}) => {
    validateAccess();

    const exportOptions = {
      ...filters,
      limit: 10000,
      offset: 0,
    };

    const result = await getTrackers(exportOptions);
    
    const exportData = result.trackers.map(tracker => ({
      'Tracker ID': tracker.tracker_id,
      'Admin ID': tracker.admin_id,
      'Change Type': tracker.change_type,
      'Change Reason': tracker.change_reason || '',
      'Change Date': tracker.change_date ? new Date(tracker.change_date).toLocaleDateString() : '',
      'Change Time': tracker.change_time || '',
    }));

    return {
      data: exportData,
      totalCount: result.total_count,
      filename: `admin_tracker_${new Date().toISOString().split('T')[0]}.csv`
    };
  }, [getTrackers, validateAccess]);

  // Get tracker summary
  const getTrackerSummary = useCallback(async () => {
    validateAccess();

    try {
      const [stats, recentTrackers, changeTypes] = await Promise.all([
        getTrackerStats(),
        getRecentTrackers(10),
        getChangeTypes(),
      ]);

      return {
        totalChanges: stats.stats.total_changes,
        changesThisMonth: stats.stats.changes_this_month,
        changesLastMonth: stats.stats.changes_last_month,
        changesToday: stats.stats.changes_today,
        uniqueAdmins: stats.stats.unique_admins,
        changesByType: stats.stats.changes_by_type,
        recentTrackers: recentTrackers.trackers,
        availableChangeTypes: changeTypes.change_types,
        accessLevel: loginType,
        hasTrackers: stats.stats.total_changes > 0,
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to get tracker summary';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getTrackerStats, getRecentTrackers, getChangeTypes, validateAccess, loginType]);

  // Health check
  const checkHealth = useCallback(async () => {
    validateAccess();

    try {
      const healthUrl = loginType === 'master' 
        ? `${API_BASE_URL}/master/admin-tracker/health`
        : `${API_BASE_URL}/admin/admin-tracker/health`;
      
      const response = await makeAuthenticatedRequest(healthUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Health check failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [makeAuthenticatedRequest, validateAccess, API_BASE_URL, loginType]);

  // Validation helpers
  const validateTrackerFilters = useCallback((filters) => {
    const errors = [];

    if (filters.limit && (filters.limit < 1 || filters.limit > 1000)) {
      errors.push({ field: 'limit', message: 'Limit must be between 1 and 1000' });
    }

    if (filters.offset && filters.offset < 0) {
      errors.push({ field: 'offset', message: 'Offset must be non-negative' });
    }

    if (filters.dateFrom && filters.dateTo && 
        new Date(filters.dateFrom) > new Date(filters.dateTo)) {
      errors.push({ field: 'dateRange', message: 'Date from must be before date to' });
    }

    if (filters.search && filters.search.length < 2) {
      errors.push({ field: 'search', message: 'Search term must be at least 2 characters' });
    }

    return errors;
  }, []);

  // Validate tracker input
  const validateTrackerInput = useCallback((trackerData) => {
    const errors = [];

    if (!trackerData.admin_id || trackerData.admin_id.trim() === '') {
      errors.push({ field: 'admin_id', message: 'Admin ID is required' });
    }

    if (!trackerData.change_type || trackerData.change_type.trim() === '') {
      errors.push({ field: 'change_type', message: 'Change type is required' });
    }

    return errors;
  }, []);

  // Format tracker data for display
  const formatTrackerData = useCallback((tracker) => {
    const getChangeTypeColor = (changeType) => {
      const colors = {
        'password_change': 'orange',
        'email_change': 'blue',
        'profile_update': 'green',
        'permission_change': 'purple',
        'login_attempt': 'gray',
        'account_activation': 'teal',
        'account_deactivation': 'red',
        'settings_change': 'cyan',
      };
      return colors[changeType] || 'gray';
    };

    const getChangeTypeIcon = (changeType) => {
      const icons = {
        'password_change': '🔑',
        'email_change': '📧',
        'profile_update': '👤',
        'permission_change': '🔒',
        'login_attempt': '🚪',
        'account_activation': '✅',
        'account_deactivation': '❌',
        'settings_change': '⚙️',
      };
      return icons[changeType] || '📝';
    };

    return {
      ...tracker,
      changeTypeColor: getChangeTypeColor(tracker.change_type),
      changeTypeIcon: getChangeTypeIcon(tracker.change_type),
      formattedChangeDate: tracker.change_date 
        ? new Date(tracker.change_date).toLocaleDateString() 
        : 'N/A',
      formattedChangeTime: tracker.change_time || 'N/A',
      formattedDateTime: tracker.change_date && tracker.change_time
        ? `${new Date(tracker.change_date).toLocaleDateString()} ${tracker.change_time}`
        : 'N/A',
      hasReason: !!tracker.change_reason && tracker.change_reason.trim() !== '',
    };
  }, []);

  // Get endpoint info for debugging
  const getEndpointInfo = useCallback(() => {
    return {
      loginType,
      baseUrl: loginType === 'master' 
        ? `${API_BASE_URL}/master/admin-tracker/api/v1/admin-tracker`
        : `${API_BASE_URL}/admin/admin-tracker/api/v1/admin-tracker`,
      accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
      requiredHeaders: loginType === 'master' 
        ? ['Authorization', 'secret_credential']
        : ['Authorization', 'admin_id'],
    };
  }, [loginType, API_BASE_URL]);

  // Common change types for reference
  const commonChangeTypes = [
    'password_change',
    'email_change',
    'profile_update',
    'permission_change',
    'login_attempt',
    'account_activation',
    'account_deactivation',
    'settings_change',
  ];

  const value = {
    // State
    loading,
    error,
    
    // Core CRUD Actions
    createTracker,
    getTrackers,
    getTrackerById,
    getTrackersByAdminId,
    deleteTracker,
    
    // Statistics Actions
    getTrackerStats,
    getTrackerStatsByAdminId,
    getChangeTypes,
    
    // Filter Actions
    getTrackersByChangeType,
    getTrackersByDateRange,
    searchTrackers,
    getRecentTrackers,
    getTodayTrackers,
    getThisMonthTrackers,
    
    // Utility Actions
    exportTrackerData,
    getTrackerSummary,
    checkHealth,
    
    // Helpers
    clearError,
    validateTrackerFilters,
    validateTrackerInput,
    formatTrackerData,
    getEndpointInfo,
    
    // Constants
    commonChangeTypes,
    
    // Computed values
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasTrackerAccess: loginType === 'admin' || loginType === 'master',
    currentAccessLevel: loginType,
  };

  return (
    <AdminTrackerContext.Provider value={value}>
      {children}
    </AdminTrackerContext.Provider>
  );
};